import { ref } from "vue"

export enum SupportedAnimatedCountDownEnum {
    days = "days",
    hours = "hours",
    minutes = "minutes",
    seconds = "seconds",
    none = "none",
}

export type SupportedAnimatedCountDown =
    keyof typeof SupportedAnimatedCountDownEnum

export interface CountDownType {
    days: number
    hours: number
    minutes: number
    seconds: number
    isAnimated: SupportedAnimatedCountDown
}

export function useCountDown(dateInit: Date, dateEnd: Date, today: Date) {
    const defaultInitialValues: CountDownType = {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        isAnimated: SupportedAnimatedCountDownEnum.none,
    }

    dateEnd.setHours(23, 59, 59, 999)

    const diffMiliseg = computed(() => dateEnd - today)

    const segTotal = Math.floor(diffMiliseg.value / 1000)
    const diffDate = ref(Math.floor(segTotal / (60 * 60 * 24)))
    const diffHours = ref(Math.floor((segTotal % (60 * 60 * 24)) / (60 * 60)))
    const diffMinutes = ref(Math.floor((segTotal % (60 * 60)) / 60))
    const diffSeconds = ref(segTotal % 60)

    const countDownEnabled = computed({
        get() {
            return (
                diffMiliseg.value > 0 && dateInit <= today && dateEnd >= today
            )
        },
        set(newValue) {
            return newValue
        },
    })

    const initializeCountDown = (
        initialValues?: CountDownType | undefined
    ): CountDownType => {
        if (initialValues) return initialValues
        return defaultInitialValues
    }

    const udpateCountDown = (): CountDownType => {
        let isAnimated = SupportedAnimatedCountDownEnum.none
        if (countDownEnabled.value) {
            diffSeconds.value -= 1
            isAnimated = SupportedAnimatedCountDownEnum.seconds
            if (diffSeconds.value < 0) {
                diffSeconds.value = 59
                diffMinutes.value -= 1
                isAnimated = SupportedAnimatedCountDownEnum.minutes
                if (diffMinutes.value < 0) {
                    diffMinutes.value = 59
                    diffHours.value -= 1
                    isAnimated = SupportedAnimatedCountDownEnum.hours
                    if (diffHours.value < 0) {
                        diffHours.value = 23
                        diffDate.value -= 1
                        isAnimated = SupportedAnimatedCountDownEnum.days
                        if (diffDate.value < 0) {
                            countDownEnabled.value = false
                            return defaultInitialValues
                        }
                    }
                }
            }

            return {
                days: diffDate.value,
                hours: diffHours.value,
                minutes: diffMinutes.value,
                seconds: diffSeconds.value,
                isAnimated: isAnimated,
            }
        }

        return defaultInitialValues
    }

    const countdown = ref<CountDownType>(initializeCountDown())

    return {
        countdown,
        countDownEnabled,
        udpateCountDown,
    }
}
